import React from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import i18n from 'i18next';
import Cookies from 'js-cookie';
import _ from 'lodash';
import fromPairs from 'lodash/fromPairs';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import moment from 'moment';
import Swiper from 'swiper';

import {
	APP_ENVIRONMENT,
	AUTHENTICATION_COOKIE_DOMAIN,
	AUTHENTICATION_COOKIE_SUFFIX,
	BUGSNAG_API_KEY,
	BUILD_VERSION,
	COOKIES,
	FRONTEND_ROUTES,
	FRONTEND_URL,
	GA4_TAG_ID,
	GTM_CONTAINER_ID,
	IS_ADMIN,
	UTM_TAGS_TTL_DAYS,
} from '@/services/Constants';

import type { DailyReport } from '@/services/types';
import type { UserData } from '@/shared/types';

export function Timer(this: any, callback: () => void, delay: number) {
	let timerId: number | undefined;
	let start: number | undefined;
	let remaining: number = delay;

	this.pause = () => {
		if (timerId !== undefined) {
			window.clearTimeout(timerId);
		}
		if (start !== undefined) {
			remaining -= new Date().getTime() - start;
		}
	};

	this.resume = () => {
		start = new Date().getTime();
		if (timerId !== undefined) {
			window.clearTimeout(timerId);
		}
		timerId = window.setTimeout(callback, remaining);
	};

	this.resume();
}

export const isSecure = () => {
	return window.location.protocol === 'https:';
};

export const getIsSafari = () => {
	const userAgent = navigator.userAgent.toLowerCase();
	const isChrome = userAgent.indexOf('chrome') > -1;
	let isSafari = userAgent.indexOf('safari') > -1;
	if (isChrome && isSafari) isSafari = false;
	return isSafari;
};

export const getUtmTagsLikeObject = (string: string): Record<string, string> => {
	return fromPairs(
		string.split('&').reduce((acc: string[][], q: string) => {
			if (q.indexOf('utm_') !== -1) acc.push(q.split('='));
			return acc;
		}, []),
	);
};

export const setUtmTags = (query: string): void => {
	const queryString = query.substr(1);
	const utmTags = getUtmTagsLikeObject(queryString);

	// eslint-disable-next-line guard-for-in
	for (const tag in utmTags) {
		setCookie(tag, utmTags[tag], {
			expires: UTM_TAGS_TTL_DAYS,
			secure: isSecure(),
		});
	}
};

export const getUtmTags = (): string => {
	return Object.entries(Cookies.get())
		.reduce((acc: string[], [key, value]: [string, string]) => {
			if (key.indexOf('utm_') !== -1) acc.push(`${key}=${value}`);
			return acc;
		}, [])
		.join('&');
};

export const removeUtmTags = (utmTagsObject: Record<string, string>): void => {
	for (const tag in utmTagsObject) {
		removeCookie(tag);
	}
};

export const deleteCharactersInString = (string: string, character: string): string => {
	return string
		.split('')
		.filter((item: string) => item !== character)
		.join('');
};

export const nameInUrlFormat = (name: string): string | undefined => {
	let convertedUrl = name;
	if (name) {
		if (name.includes('%')) {
			convertedUrl = deleteCharactersInString(name, '%');
		}

		if (name.includes('#')) {
			convertedUrl = deleteCharactersInString(convertedUrl, '#');
		}

		return convertedUrl
			.trim()
			.split(' ')
			.join('-')
			.split('/')
			.join('-')
			.split('.')
			.join('')
			.split('?')
			.join('')
			.toLowerCase();
	}
	return name;
};

export const getCookie = (key: string): string | undefined => {
	if (isAuthenticationCookie(key)) {
		const cookie = Cookies.get(key + AUTHENTICATION_COOKIE_SUFFIX);
		if (cookie) return cookie;
	}
	return Cookies.get(key);
};

function isAuthenticationCookie(key: string): boolean {
	const authenticationCookieKeys: string[] = [
		COOKIES.accessToken,
		COOKIES.tokenValidUntil,
		COOKIES.temporaryToken,
		COOKIES.renewTime,
		COOKIES.loggedIn,
		COOKIES.secureSession,
	];
	return _(COOKIES).pick(authenticationCookieKeys).values().value().includes(key);
}

export const setCookie = (
	key: string,
	val: string | number,
	options: Record<string, any> = {},
): void => {
	if (isAuthenticationCookie(key)) {
		key = key + AUTHENTICATION_COOKIE_SUFFIX;
		if (AUTHENTICATION_COOKIE_DOMAIN) {
			options.domain = AUTHENTICATION_COOKIE_DOMAIN;
		}
	}
	Cookies.set(key, val as string, options);
};

export const removeCookie = (key: string, options: Record<string, any> = {}): void => {
	Cookies.remove(key, options);
	if (isAuthenticationCookie(key)) {
		if (AUTHENTICATION_COOKIE_DOMAIN) {
			options.domain = AUTHENTICATION_COOKIE_DOMAIN;
		}
		Cookies.remove(key + AUTHENTICATION_COOKIE_SUFFIX, options);
	}
};

export const createSwipe = () => {
	// eslint-disable-next-line no-new
	new Swiper('.swiper-container', {
		slidesPerView: 'auto',
		freeMode: true,
		roundLengths: true,
		watchOverflow: true,
	});
};

export const getTimeRange = (created_at: string | Date): string => {
	const posted = new Date().getTime() - new Date(created_at).getTime();

	const years = Math.round(posted / 31557600000);
	const months = Math.round(posted / 2629800000);
	const weeks = Math.round(posted / 604800000);
	const days = Math.round(posted / 86400000);
	const hours = Math.round(posted / 3600000);
	const minutes = Math.round(posted / 60000);

	if (years >= 1) {
		return `${years} ${i18n.t('general.year')} ${i18n.t('general.past')}`;
	}
	if (months >= 1) {
		return `${months} ${i18n.t('general.month', { count: months })} ${i18n.t('general.past')}`;
	}
	if (weeks >= 1) {
		return `${weeks} ${i18n.t('general.week', { count: weeks })} ${i18n.t('general.past')}`;
	}
	if (days >= 1) {
		return `${days} ${i18n.t('general.day', { count: days })} ${i18n.t('general.past')}`;
	}
	if (hours >= 1) {
		return `${hours} ${i18n.t('general.hour')} ${i18n.t('general.past')}`;
	}

	return minutes <= 1
		? i18n.t('general.just_now')
		: `${minutes} ${i18n.t('general.minutes')} ${i18n.t('general.past')}`;
};

export const validateVimeoVideoId = (
	e: React.ChangeEvent<HTMLInputElement> | string,
	setVideoId: (id: string) => void,
): string | null => {
	const val = typeof e === 'string' ? e : e.target.value;

	if (val && val.trim()) {
		if (val.includes('vimeo.com')) {
			const valArray = val.split('/');
			const valId = valArray[valArray.length - 1];

			if (valId && valId.match(/^\d{8}/)) {
				setVideoId(valArray[valArray.length - 1]);
				return null;
			}
			return i18n.t('general.invalid_value');
		}
		if (!val.match(/^\d{8}/)) {
			return i18n.t('general.invalid_value');
		}
	}
	return null;
};

export const extractVimeoId = (contentUrl: string): string => {
	const match = contentUrl.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
	return match ? match[1] : contentUrl;
};

export const setLocalStorageItem = (key: string, value: string): void => {
	window.localStorage.setItem(key, value);
};
export const getLocalStorageItem = (key: string): string | null => window.localStorage.getItem(key);
export const removeLocalStorageItem = (key: string): void => {
	window.localStorage.removeItem(key);
};

export const setSessionStorageItem = (key: string, value: string): void => {
	window.sessionStorage.setItem(key, value);
};
export const getSessionStorageItem = (key: string): string | null =>
	window.sessionStorage.getItem(key);
export const removeSessionStorageItem = (key: string): void => {
	window.sessionStorage.removeItem(key);
};

export const convertImageURLToBlob = async (imageUrl: string): Promise<Blob> => {
	const response = await fetch(imageUrl);
	return response.blob();
};

export const formatDate = (date: string | Date): string => moment(date).format('DD-MM-YYYY');

export const stripHtmlAndDecodeEntities = (html: string): string => {
	if (!html) return '';

	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
		const container = doc.body?.firstChild;
		return container?.textContent || '';
	} catch {
		// Fallback: use a temporary element
		const temp = document.createElement('div');
		temp.innerHTML = html;
		return temp.textContent || temp.innerText || '';
	}
};

export const getHTMLCollection = (
	innerHTML: string,
	classname: string,
): HTMLCollectionOf<Element> => {
	const tmp = document.createElement('div');
	tmp.innerHTML = innerHTML || '';
	return tmp.getElementsByClassName(classname);
};

export const encodingShareUrl = (url: string): string => url.replace('%26', '&');

export const formatSecondsTime = (sec: number): string => moment.utc(sec * 1000).format('mm:ss');

export const getRandomValueFromRange = (min: number, max: number): number =>
	Math.floor(Math.random() * (max - min + 1)) + min;

export const setGTMData = (GTMData: Record<string, any>): void => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(GTMData);
};

export const slugify = (str: string): string =>
	str
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, '')
		.replace(/[\s_-]+/g, '-')
		.replace(/^-+|-+$/g, '');

export const getRandomInt = (min: number, max: number): number => {
	min = Math.ceil(min);
	max = Math.floor(max);
	return Math.floor(Math.random() * (max - min) + min);
};

export const convertQueryToObject = (query: string): Record<string, string> => {
	const urlParams = new URLSearchParams(query.replace(/\?/g, ''));
	const entries = urlParams.entries();
	const result: Record<string, string> = {};
	for (const [key, value] of entries) {
		result[key] = value;
	}
	return result;
};

export const removeHtmlTags = (html: string): string => {
	if (!html) return '';

	const div = document.createElement('div');
	div.innerHTML = html;
	return div.textContent || div.innerText || '';
};

export const identifyHotjar = (userData: UserData, dailyReport: DailyReport): void => {
	if (!window.hj) return;

	const { id, full_name, child_name, grade, finished_daily_goal } = userData || {};
	const { minutes_played } = dailyReport || {};

	window.hj('identify', id, {
		child_name,
		parent_name: full_name,
		grade: grade?.title,
		learning_path_length: minutes_played,
		finished_daily_goal,
	});
};

export const sendHotjarEvent = (eventName: string): void => {
	window?.hj && window.hj('event', eventName);
};

export const setBugsnagUser = (userId: string): void => {
	Bugsnag.isStarted() && Bugsnag.setUser(userId);
};

export const initializeGtag = () => {
	if (!GTM_CONTAINER_ID) return;

	window.gtag = function () {
		dataLayer?.push(arguments);
	};

	gtag('js', new Date());
	gtag('config', GA4_TAG_ID);

	new Promise((resolve) => {
		gtag('get', GA4_TAG_ID, 'client_id', resolve);
	}).then((data) => {
		window.ga4ClientId = data;
	});

	new Promise((resolve) => {
		gtag('get', GA4_TAG_ID, 'session_id', resolve);
	}).then((data) => {
		window.ga4SessionId = data;
	});
};

export const initializeBugsnag = (): BugsnagErrorBoundary | null => {
	let ErrorBoundary: BugsnagErrorBoundary | null = null;

	if (BUGSNAG_API_KEY !== null && !FRONTEND_URL.includes('localhost')) {
		Bugsnag.start({
			apiKey: BUGSNAG_API_KEY,
			plugins: [new BugsnagPluginReact()],
			appVersion: BUILD_VERSION,
			autoDetectErrors: true,
			releaseStage: APP_ENVIRONMENT,
			onError: (event) => {
				if (event.originalError?.message === 'Request failed with status code 401') {
					return false;
				}
				return true;
			},
		});

		ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React) || null;
	}

	return ErrorBoundary;
};

export const initializeLogRocket = (userId?: string): void => {
	if (APP_ENVIRONMENT === 'production' && !IS_ADMIN) {
		LogRocket.init('futurewhiz/studira', {
			network: {
				requestSanitizer: (request) => {
					const sensitiveEndpoints = [
						'get_user_data',
						'users',
						'subscriptions',
						'auth/get_token',
					];
					if (
						sensitiveEndpoints.some((endpoint) =>
							request.url.toLowerCase().includes(endpoint),
						)
					) {
						return null;
					}
					return request;
				},
			},
		});
		setupLogRocketReact();
		if ((LogRocket as any)._isInitialized && userId) {
			LogRocket.identify(userId);
		}
	}
};

export const isValidUrl = (urlString: string): boolean => {
	const urlPattern = new RegExp(
		'(?:https?)://(\\w+:?\\w*)?(\\S+)(:\\d+)?(/|/([\\w#!:.?+=&%!\\-/]))?',
	);
	return !!urlPattern.test(urlString);
};

export const capitalizeFirstLetter = (str: string): string => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

export const lowercaseFirstLetter = (str: string): string => {
	return str.charAt(0).toLowerCase() + str.slice(1);
};

export const getQueryString = (history: { location: { search: string } }): string => {
	return history.location.search;
};

export const getQueryObject = (query: string): Record<string, string> => {
	return fromPairs(
		query
			.substr(1)
			.split('&')
			.map((s) => s.split('=')),
	);
};

export const getUpdatedQuery = (queryObject: Record<string, string>): string => {
	return Object.entries(queryObject).reduce((acc: string, [k, v]: [string, string]) => {
		acc = acc ? `${acc}&${k}=${v}` : `${k}=${v}`;
		return acc;
	}, '');
};

export const getExercisePath = (title: string, id: string): string => {
	return FRONTEND_ROUTES.EXERCISE.replace(':section', slugify(title)).replace(':id', id);
};

export const getClientDeviceId = (): string => {
	const deviceId = getSessionStorageItem('clientDeviceId');
	if (!deviceId) {
		const newDeviceId = Math.random().toString(36).substring(2, 15);
		setSessionStorageItem('clientDeviceId', newDeviceId);
		return newDeviceId;
	}
	return deviceId;
};

export const generateNewClientDeviceId = (): string => {
	const newDeviceId = Math.random().toString(36).substring(2, 15);
	setSessionStorageItem('clientDeviceId', newDeviceId);
	return newDeviceId;
};

export const isOfflineError = (error: any): boolean => {
	return (
		(typeof navigator !== 'undefined' && !navigator.onLine) ||
		!error?.response ||
		error?.code === 'ERR_NETWORK' ||
		(error?.message && /Network Error/i.test(error.message))
	);
};

export const downloadFile = async (url: string): Promise<void> => {
	try {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
		}
		const blob = await response.blob();
		const blobUrl = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = blobUrl;
		a.download = url.split('/').pop() ?? '';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(blobUrl);
	} catch (error) {
		Bugsnag.isStarted() && Bugsnag.notify(error as Error);
		console.error('downloadFile error:', error);
	}
};
